import {
  CSSProperties,
  FC,
  forwardRef,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';

import { Icons } from '@beeline/design-tokens/js/iconfont';

import clsx from 'clsx';
import { Parking, Rect } from 'src/types';

import { TooltipWrapper } from '../TooltipWrapper/TooltipWrapper';

import { Content } from './Content';
import classes from './ParkingPlace.module.scss';

const borderRadiusCoef = 0.3;

export interface ParkingPlaceLayoutProps {
  item: Parking;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  onMouseOver?: (item: Parking) => void;
  onMouseLeave?: (item: Parking) => void;
  icon?: Icons;
}

export const ParkingPlaceLayout = forwardRef<
  HTMLDivElement,
  ParkingPlaceLayoutProps
>(function Inner(
  {
    item,
    className,
    contentClassName,
    disabled,
    onMouseOver,
    onMouseLeave,
    icon,
  },
  ref,
) {
  const { width, height } = item;

  const x = item.x - width / 2;
  const y = item.y - height / 2;
  const borderRadius = borderRadiusCoef * width;

  const containerStyle = useMemo(
    (): CSSProperties => ({
      transform: `translate(${x}px,${y}px)`,
      width: `${width}px`,
      height: `${height}px`,
      borderRadius: `${borderRadius}px`,
    }),
    [borderRadius, height, width, x, y],
  );

  const id = item.mapPlaceId;
  const placeId = disabled ? undefined : id;

  const handleMouseOver = useCallback(() => {
    onMouseOver?.(item);
  }, [item, onMouseOver]);

  const handleMouseLeave = useCallback(() => {
    onMouseLeave?.(item);
  }, [item, onMouseLeave]);

  return (
    <div
      className={clsx(
        classes.container,
        disabled ? classes.disabledContainer : classes.enabledContainer,
        className,
      )}
      style={containerStyle}
      data-place-id={placeId}
      data-testid={disabled ? undefined : `place-${id}`}
      ref={ref}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <Content item={item} contentClassName={contentClassName} icon={icon} />
    </div>
  );
});

export interface ParkingPlaceProps extends ParkingPlaceLayoutProps {
  title?: ReactNode;
}

export const ParkingPlace: FC<ParkingPlaceProps> = ({ title, ...rest }) => {
  return title ? (
    <TooltipWrapper title={title}>
      <ParkingPlaceLayout {...rest} />
    </TooltipWrapper>
  ) : (
    <ParkingPlaceLayout {...rest} />
  );
};

export const parkingToRect = (parking: Parking): Rect => {
  const halfWidth = parking.width / 2;
  const halfHeight = parking.height / 2;

  return {
    type: 'rect',
    left: parking.x - halfWidth,
    right: parking.x + halfWidth,
    top: parking.y - halfHeight,
    bottom: parking.y + halfHeight,
  };
};
